import { recordOperationOutcome, reconcileOperationDiagnostics, isUnknownAcknowledgement } from '../utils/operationDiagnostics';
import { isObservabilityEnabled } from '../utils/observabilityConfig';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import localforage from 'localforage';
import { notificationManager } from '../utils/notificationManager';
import { useOfflineModeContext } from '../context/offlineContext';
import { useOfflineStatusContext } from '../context/offlineStatusContext';
import { registerStore } from '../utils/offlineStoreRegistry';
import {
  getSyncService,
  isSyncServiceInitialized,
} from '../services/offlineSyncService';
import { checkGraphQLHealth } from '../utils/graphqlHealthCheck';
import { reportOfflineSyncError, reportOfflineQueueError } from '../utils/sentryHelpers';
import {
  createQueueError,
  getQueueError,
  readOperationQueue,
  writeOperationQueue,
} from '../utils/operationQueue';
import { ensureValidTokenBeforeSync } from '../components/ApolloProvider';
import {
  getFailedStore,
  calculateQueueSize,
  isRetriableSyncError,
} from '../utils/offlineSyncHelpers';
import { getTaskUnavailableMessage } from '../utils/taskSyncErrors';
import { useInsertEventLog } from './useInsertEventLog';
import { requiresPricingReview } from '../utils/orderPricingReview';
import { enqueueFieldPhotoOperation, removeQueuedFieldPhotoOperations, discardFieldPhotoOperations } from '../utils/fieldPhotos';

export const useOfflineSync = (
  storeName,
  operationHandlers = {},
  redirectLink = '',
) => {
  const [pendingOperations, setPendingOperations] = useState([]);
  const lastAttemptFailedRef = useRef(false);
  const processingRef = useRef(false);
  const retryCountRef = useRef(new Map());
  const autoRetryTimeoutRef = useRef(null);
  const processPendingOperationsRef = useRef(null);
  const { networkState } = useOfflineModeContext();
  const {
    refreshStatus,
    isStoreProcessing,
    markStoreAsProcessing,
    markStoreAsDone,
    autoSyncEnabled,
  } = useOfflineStatusContext();
  const isOnline = networkState.online;
  const isProcessing = isStoreProcessing(storeName);
  const { insertEvent } = useInsertEventLog();

  const operationStore = useMemo(() => {
    if (!storeName) return null;
    return localforage.createInstance({
      name: `OfflineOperations_${storeName}`,
    });
  }, [storeName]);

  useEffect(() => {
    if (storeName) {
      registerStore(storeName, redirectLink);
    }
  }, [storeName, redirectLink]);

  const reloadOperations = useCallback(async () => {
    if (!operationStore) return;
    setPendingOperations((await operationStore.getItem('operations')) || []);
    await refreshStatus(false);
  }, [operationStore, refreshStatus]);

  useEffect(() => {
    if (storeName !== 'field_photos') return;
    const reload = () => reloadOperations().catch(() => {});
    window.addEventListener('field-photos-changed', reload);
    return () => window.removeEventListener('field-photos-changed', reload);
  }, [storeName, reloadOperations]);

  useEffect(() => {
    const loadPendingOperations = async () => {
      if (!operationStore) return;
      try {
        lastAttemptFailedRef.current = false;
        const operations = (await operationStore.getItem('operations')) || [];
        setPendingOperations(operations);
        reconcileOperationDiagnostics(operations);
      } catch (error) {
        console.error(`Error loading pending ops for ${storeName}:`, error);
      }
    };
    loadPendingOperations();
  }, [operationStore, storeName]);

  useEffect(() => {
    if (!operationStore) return undefined;
    const reconcile = () => {
      if (document.visibilityState === 'visible' && isObservabilityEnabled('outcomes')) {
        operationStore.getItem('operations').then((operations) => reconcileOperationDiagnostics(operations || [])).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', reconcile);
    return () => document.removeEventListener('visibilitychange', reconcile);
  }, [operationStore]);

  useEffect(() => {
    if (isOnline) {
      lastAttemptFailedRef.current = false;
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }
    }
  }, [isOnline]);

  useEffect(() => {
    return () => {
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
      }
    };
  }, []);

  const isRetriableError = useCallback(
    (error, operation = null) => isRetriableSyncError(error, operation),
    [],
  );

  const scheduleAutoRetry = useCallback((delayMs) => {
    if (autoRetryTimeoutRef.current) {
      clearTimeout(autoRetryTimeoutRef.current);
    }

    autoRetryTimeoutRef.current = setTimeout(() => {
      lastAttemptFailedRef.current = false;
      if (processPendingOperationsRef.current) {
        processPendingOperationsRef.current(false);
      }
    }, delayMs);
  }, []);

  const processPendingOperations = useCallback(
    async (isManualTrigger = false) => {
      if (processingRef.current || isStoreProcessing(storeName)) {
        return;
      }

      if (!isManualTrigger && lastAttemptFailedRef.current) {
        return;
      }

      const currentOps = (await operationStore?.getItem('operations')) || [];
      const actionableOps = currentOps.filter((op) => !requiresPricingReview(op));
      if (
        !operationStore ||
        !isOnline ||
        !networkState.online ||
        actionableOps.length === 0
      ) {
        lastAttemptFailedRef.current = false;
        return;
      }

      const isServerReachable = await checkGraphQLHealth();
      if (!isServerReachable) {
        console.warn(
          `GraphQL server unreachable, skipping sync for ${storeName}`,
        );
        return;
      }

      const tokenCheck = await ensureValidTokenBeforeSync();
      if (!tokenCheck.valid) {
        console.warn(
          `[${storeName}] Token not valid before sync (reason: ${tokenCheck.reason}), skipping`,
        );
        if (tokenCheck.reason === 'no_user') {
          notificationManager.showWarning(
            `Sinhronizacija odgođena — niste prijavljeni. Prijavite se ponovo.`,
          );
        } else {
          notificationManager.showWarning(
            `Sinhronizacija odgođena — neuspješna autentifikacija. Provjerite mrežu i pokušajte ponovo.`,
          );
        }
        lastAttemptFailedRef.current = true;
        scheduleAutoRetry(15000); // Try again in 15s
        return;
      }

      lastAttemptFailedRef.current = false;
      markStoreAsProcessing(storeName);
      processingRef.current = true;

      let successOperations = 0;
      let errorOccurredInBatch = false;
      let processedIds = new Set();
      const operationsToProcess = [...currentOps];

      try {
        const syncService = isSyncServiceInitialized()
          ? getSyncService()
          : null;

        const useLegacyHandlers =
          !syncService && Object.keys(operationHandlers).length > 0;

        if (!syncService && !useLegacyHandlers) {
          console.error(
            `[${storeName}] SyncService not initialized and no legacy handlers provided`,
          );
          errorOccurredInBatch = true;
          lastAttemptFailedRef.current = true;
          return;
        }

        for (const operation of operationsToProcess) {
          if (processedIds.has(operation.id)) continue;
          if (requiresPricingReview(operation)) continue;
          if (!networkState.online) {
            errorOccurredInBatch = true;
            break;
          }

          try {
            recordOperationOutcome(operation, 'sending', { source: 'automatic' });
            if (syncService) {
              await syncService.executeOperation(operation);
            } else {
              const executeFn = operationHandlers[operation.type];
              if (!executeFn) {
                console.error(
                  `Unknown op type: ${operation.type} in ${storeName}`,
                );
                processedIds.add(operation.id);
                continue;
              }
              await executeFn(operation.variables, operation.id);
            }

            retryCountRef.current.delete(operation.id);
            processedIds.add(operation.id);
            successOperations++;

            const freshOps = (await operationStore.getItem('operations')) || [];
            const safeBase = freshOps.length === 0 && operationsToProcess.length > 0 ? operationsToProcess : freshOps;
            const remainingOperations = safeBase.filter(
              (op) => !processedIds.has(op.id),
            );
            if (storeName === 'field_photos') await removeQueuedFieldPhotoOperations(processedIds);
            else await operationStore.setItem('operations', remainingOperations);

            if (retryCountRef.current.size > 50) {
              const remainingIds = new Set(
                remainingOperations.map((op) => op.id),
              );
              for (const [id] of retryCountRef.current) {
                if (!remainingIds.has(id)) {
                  retryCountRef.current.delete(id);
                }
              }
            }
          } catch (error) {
            console.error(
              `Error processing op ${operation.id} (${operation.type}) in ${storeName}:`,
              error,
            );

            const currentRetries = retryCountRef.current.get(operation.id) || 0;
            const MAX_RETRIES = 3;
            const BACKOFF_DELAYS = [2000, 10000, 30000];
            const isRetriable = isRetriableError(error, operation);
            const userMessage =
              getTaskUnavailableMessage(error, operation) ||
              error.message ||
              'Unknown error';

            recordOperationOutcome(operation, isUnknownAcknowledgement(error) ? 'acknowledgement_unknown' : 'retrying', { source: 'automatic', reason: isUnknownAcknowledgement(error) ? 'network' : 'none' });
            reportOfflineSyncError(error, operation, storeName, currentRetries);

            if (isRetriable && currentRetries < MAX_RETRIES) {
              retryCountRef.current.set(operation.id, currentRetries + 1);
              const delayMs = BACKOFF_DELAYS[currentRetries] || 30000;

              notificationManager.showWarning(
                `[${storeName}] Operacija neuspješna (privremena greška), pokušat ćemo ponovno za ${Math.round(delayMs / 1000)}s (${currentRetries + 1}/${MAX_RETRIES})`,
              );

              scheduleAutoRetry(delayMs);
              errorOccurredInBatch = true;

              if (error.networkError || error.message?.includes('Failed to fetch') || error.message?.includes('timeout')) {
                break;
              }

              if (operation.type === 'ANSWER_SURVEY') {
                break;
              }

              continue;
            } else {
              const reason = !isRetriable
                ? 'greška validacije (neponovljiva)'
                : `max pokušaja (${MAX_RETRIES}) dostignut`;

              console.error(
                `[${storeName}] Moving op ${operation.id} to failed queue: ${reason}`,
              );

              const failedStore = getFailedStore(storeName);
              const failedOps = (await failedStore.getItem('operations')) || [];
              failedOps.push({
                ...operation,
                failedAt: new Date().toISOString(),
                failureReason: userMessage,
                retryAttempts: isRetriable ? MAX_RETRIES : 0,
              });

              if (operation.type === 'ANSWER_SURVEY') {
                const pendingTokens = new Set();
                (operation.variables?.input?.answers || []).forEach((answer) => {
                  if (
                    typeof answer.choice === 'string' &&
                    answer.choice.startsWith('pending_upload_')
                  ) {
                    pendingTokens.add(answer.choice);
                  }
                  if (answer.distributionSelections) {
                    try {
                      Object.values(
                        JSON.parse(answer.distributionSelections),
                      ).forEach((selection) => {
                        (selection?.fotografije || []).forEach((photo) => {
                          if (photo?.pendingToken) {
                            pendingTokens.add(photo.pendingToken);
                          }
                        });
                      });
                    } catch (parseErr) {
                      // malformed selections — nothing to link
                    }
                  }
                });

                operationsToProcess.forEach((op) => {
                  if (
                    op.type === 'UPLOAD_SURVEY_PHOTO' &&
                    !processedIds.has(op.id) &&
                    pendingTokens.has(op.variables?.pendingToken)
                  ) {
                    failedOps.push({
                      ...op,
                      failedAt: new Date().toISOString(),
                      failureReason:
                        'Zavisna operacija: anketa nije sinhronizovana',
                      retryAttempts: 0,
                    });
                    processedIds.add(op.id);
                    retryCountRef.current.delete(op.id);
                  }
                });
              }

              await failedStore.setItem('operations', failedOps);
              recordOperationOutcome(operation, 'failed_queue', { source: 'automatic', reason: isRetriable ? 'retry_limit' : 'validation' });

              notificationManager.showError(
                `[${storeName}] Operacija premještena u red neuspjelih (${reason}). Provjerite u Sinhronizacija -> Neuspjele operacije.`,
              );

              processedIds.add(operation.id);
              retryCountRef.current.delete(operation.id);

              const freshAfterFailed = (await operationStore.getItem('operations')) || [];
              const safeBaseAfterFailed = freshAfterFailed.length === 0 && operationsToProcess.length > 0 ? operationsToProcess : freshAfterFailed;
              const remainingAfterFailed = safeBaseAfterFailed.filter(
                (op) => !processedIds.has(op.id),
              );
              if (storeName === 'field_photos') await removeQueuedFieldPhotoOperations(processedIds);
              else await operationStore.setItem('operations', remainingAfterFailed);

              errorOccurredInBatch = true;

              continue;
            }
          }
        }
      } finally {
        const finalRemainingOps =
          (await operationStore?.getItem('operations')) || [];
        setPendingOperations(finalRemainingOps);

        if (successOperations > 0) {
          localStorage.setItem('msales_lastSyncTime', new Date().toISOString());
          notificationManager.showSuccess(
            `[${storeName}] Uspješno sinhronizirano ${successOperations} operacija`,
          );
        }

        if (
          errorOccurredInBatch &&
          successOperations < operationsToProcess.length
        ) {
          notificationManager.showWarning(
            `[${storeName}] Sinhronizacija zaustavljena zbog greške. Preostalo: ${finalRemainingOps.length}`,
          );
        }

        markStoreAsDone(storeName);
        processingRef.current = false;

        if (refreshStatus) {
          try {
            refreshStatus(false);
          } catch (contextError) {
            console.error(
              `Error calling refreshStatus from ${storeName}:`,
              contextError,
            );
          }
        }
      }
    },
    [
      operationStore,
      isOnline,
      operationHandlers,
      storeName,
      refreshStatus,
      networkState.online,
      isStoreProcessing,
      markStoreAsProcessing,
      markStoreAsDone,
      isRetriableError,
      scheduleAutoRetry,
    ],
  );

  useEffect(() => {
    processPendingOperationsRef.current = processPendingOperations;
  }, [processPendingOperations]);

  useEffect(() => {
    let syncTimeout;

    if (
      autoSyncEnabled &&
      isOnline &&
      pendingOperations.length > 0 &&
      !processingRef.current &&
      !isStoreProcessing(storeName) &&
      !lastAttemptFailedRef.current
    ) {
      syncTimeout = setTimeout(() => {
        processPendingOperations(false);
      }, 500);
    }

    return () => {
      if (syncTimeout) clearTimeout(syncTimeout);
    };
  }, [
    autoSyncEnabled,
    isOnline,
    pendingOperations.length,
    storeName,
    isStoreProcessing,
    processPendingOperations,
  ]);

  const queueOperation = useCallback(
    async (operation) => {
      if (!operationStore)
        return { success: false, error: createQueueError('QUEUE_NOT_INITIALIZED', 'Red za slanje nije spreman. Pokušajte ponovo.') };
      if (!operation || !operation.id || !operation.type)
        return { success: false, error: createQueueError('QUEUE_INVALID_OPERATION', 'Operacija nema ispravnu oznaku ili tip. Obratite se podršci.') };

      if (storeName === 'field_photos') {
        try { await enqueueFieldPhotoOperation(operation); return { success: true, operationId: operation.id }; }
        catch (error) { return { success: false, error }; }
      }

      try {
        await registerStore(storeName, redirectLink, true);
        const storedOperation = {
          id: operation.id,
          type: operation.type,
          variables: operation.variables,
          ...(operation.diagnostics
            ? { diagnostics: operation.diagnostics }
            : {}),
          createdAt: new Date().toISOString(),
          customer: operation.customer,
          orderType: operation.orderType,
          cartItems: operation.cartItems,
          needsPricingReview: operation.needsPricingReview,
          usesGrossPrices: operation.usesGrossPrices,
        };
        const currentOperations = await readOperationQueue(operationStore, (count) =>
          reportOfflineQueueError(
            createQueueError('QUEUE_QUARANTINED', `${count} neispravnih stavki izdvojeno iz reda za slanje.`),
            { id: 'quarantine', type: storeName },
            storeName,
          ),
        );

        if (currentOperations.some((op) => op.id === operation.id)) {
          setPendingOperations(currentOperations);
          lastAttemptFailedRef.current = false;
          return { success: true, operationId: operation.id, isDuplicate: true };
        }

        const MAX_AGE_DAYS = 7;
        const WARN_AGE_DAYS = 4;
        const MAX_QUEUE_SIZE = 500;
        const MAX_QUEUE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
        const now = Date.now();
        let expiredCount = 0;
        let warningCount = 0;
        const validOperations = currentOperations.filter((op) => {
          if (op.type === 'INSERT_ORDER' || op.type === 'UPLOAD_FIELD_PHOTO' || op.type === 'UPDATE_FIELD_PHOTO_CONTEXT') return true;
          const ageDays = Math.floor((now - new Date(op.createdAt).getTime()) / (1000 * 60 * 60 * 24));
          if (ageDays >= MAX_AGE_DAYS) {
            expiredCount++;
            return false;
          }
          if (ageDays >= WARN_AGE_DAYS) {
            warningCount++;
          }
          return true;
        });

        if (expiredCount > 0) {
          notificationManager.showError(
            `${expiredCount} operacija je automatski uklonjena jer je čekala više od ${MAX_AGE_DAYS} dana bez sinhronizacije. Povežite se na internet redovno.`,
          );
        }

        if (warningCount > 0) {
          notificationManager.showWarning(
            `${warningCount} operacija čeka više od ${WARN_AGE_DAYS} dana. Sinhronizujte što prije da ne izgubite podatke!`,
          );
        }

        if (validOperations.length >= MAX_QUEUE_SIZE) {
          console.error(
            `[${storeName}] Queue full (${validOperations.length}/${MAX_QUEUE_SIZE})`,
          );
          throw createQueueError('QUEUE_FULL', `Red čekanja je pun (${MAX_QUEUE_SIZE} operacija). Sinhronizujte postojeće operacije pa pokušajte ponovo.`);
        }

        const currentQueueSize = calculateQueueSize(validOperations);
        const totalSize =
          currentQueueSize + calculateQueueSize([storedOperation]);

        if (totalSize >= MAX_QUEUE_SIZE_BYTES) {
          const sizeMB = (totalSize / (1024 * 1024)).toFixed(1);
          console.warn(
            `[${storeName}] Queue size large: ${sizeMB}MB. Consider syncing soon.`,
          );
          notificationManager.showWarning(
            `Red čekanja je velik (${sizeMB}MB). Preporučujemo sinhronizaciju.`,
          );
        }

        if (operation.type === 'CREATE_VISIT' && operation.variables?.input) {
          const visitDuplicate = validOperations.some((op) => {
            if (op.type !== 'CREATE_VISIT' || !op.variables?.input) return false;

            const opInput = op.variables.input;
            const newInput = operation.variables.input;

            const timeDiff = Math.abs(new Date() - new Date(op.createdAt));
            return (
              opInput.customer === newInput.customer &&
              opInput.description === newInput.description &&
              timeDiff < 2 * 60 * 1000
            );
          });

          if (visitDuplicate) {
            console.warn(
              `Rapid duplicate visit detected for customer ${operation.variables.input.customer}. Skipping.`,
            );
            return {
              success: true,
              operationId: operation.id,
              isDuplicate: true,
            };
          }
        }

        const updatedOperations = [...validOperations, storedOperation];
        await writeOperationQueue(operationStore, updatedOperations);
        recordOperationOutcome(operation, 'deferred_offline', { source: 'checkout' });
        setPendingOperations(updatedOperations);
        lastAttemptFailedRef.current = false;

        try {
          const input = operation.variables?.input;
          const customerId = input?.customerID || input?.customer ||
            operation.customer?.id || operation.customer;
          const opData = {
            id: operation.id,
            type: operation.type,
            cust: customerId,
            total: input?.total,
            items: input?.items?.map(i => ({ id: i.id, qty: i.quantity })) || [],
            ...(operation.diagnostics
              ? { diagnostics: operation.diagnostics }
              : {}),
          };
          Promise.resolve(insertEvent({
            eventCode: 'QUEUE_OP',
            customer: customerId && Number.isFinite(Number(customerId))
              ? Number(customerId) : null,
            document: operation.id,
            remark: JSON.stringify(opData).substring(0, 3900),
          })).catch(() => {});
        } catch (error) {
          console.warn('Failed to log queue event:', error);
        }

        if (refreshStatus) {
          Promise.resolve().then(() => refreshStatus(false)).catch(() => {});
        }

        return { success: true, operationId: operation.id };
      } catch (error) {
        recordOperationOutcome(operation, 'local_write_failed', { source: 'checkout', reason: 'storage' });
        console.error(`Error queuing operation for ${storeName}:`, error);
        const queueError = getQueueError(error);
        reportOfflineQueueError(queueError, operation, storeName);
        return { success: false, error: queueError };
      }
    },
    [operationStore, storeName, redirectLink, refreshStatus, insertEvent],
  );

  const forceSync = useCallback(() => {
    if (isOnline && !processingRef.current && !isStoreProcessing(storeName)) {
      lastAttemptFailedRef.current = false;
      processPendingOperations(true);
    } else if (!isOnline) {
      notificationManager.showInfo(
        'Niste povezani na internet za sinhronizaciju.',
      );
    } else if (processingRef.current || isStoreProcessing(storeName)) {
      notificationManager.showInfo(
        `Sinkronizacija za ${storeName} je vec u toku.`,
      );
    }
  }, [isOnline, storeName, isStoreProcessing, processPendingOperations]);

  const clearPendingOperations = useCallback(async () => {
    if (!operationStore)
      return { success: false, error: 'Store not initialized' };
    try {
      if (storeName === 'field_photos') await discardFieldPhotoOperations((await operationStore.getItem('operations')) || []);
      else await operationStore.setItem('operations', []);
      setPendingOperations([]);
      lastAttemptFailedRef.current = false;

      if (refreshStatus) {
        try {
          refreshStatus(false);
        } catch (e) {
          console.error('Error refreshing status', e);
        }
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error(
        `Error clearing pending operations for ${storeName}:`,
        error,
      );
      return { success: false, error };
    }
  }, [operationStore, storeName, refreshStatus]);

  const getFailedOperations = useCallback(async () => {
    try {
      const failedStore = getFailedStore(storeName);
      const failedOps = (await failedStore.getItem('operations')) || [];
      return failedOps;
    } catch (error) {
      console.error(`Error getting failed operations for ${storeName}:`, error);
      return [];
    }
  }, [storeName]);

  const retryFailedOperation = useCallback(
    async (operationId) => {
      try {
        const failedStore = getFailedStore(storeName);
        const failedOps = (await failedStore.getItem('operations')) || [];
        const operationToRetry = failedOps.find((op) => op.id === operationId);

        if (!operationToRetry) {
          return {
            success: false,
            error: 'Operation not found in failed queue',
          };
        }

        const { failedAt, failureReason, retryAttempts, ...originalOperation } =
          operationToRetry;

        retryCountRef.current.delete(operationId);
        const result = await queueOperation(originalOperation);

        if (result.success) {
          const latestFailed = (await failedStore.getItem('operations')) || [];
          await failedStore.setItem('operations', latestFailed.filter((op) => op.id !== operationId));
          notificationManager.showSuccess(
            `Operacija ${operationId} vraćena u red za sinhronizaciju.`,
          );
          if (isOnline && !processingRef.current) {
            processPendingOperations(true);
          }
        }

        return result;
      } catch (error) {
        console.error(`Error retrying failed operation ${operationId}:`, error);
        return { success: false, error };
      }
    },
    [storeName, queueOperation, isOnline, processPendingOperations],
  );

  const clearFailedOperations = useCallback(async () => {
    try {
      const failedStore = getFailedStore(storeName);
      await failedStore.setItem('operations', []);
      notificationManager.showSuccess(
        `Neuspjele operacije za ${storeName} su obrisane.`,
      );
      return { success: true };
    } catch (error) {
      console.error(
        `Error clearing failed operations for ${storeName}:`,
        error,
      );
      return { success: false, error };
    }
  }, [storeName]);

  if (!storeName) {
    return {
      storeName: '',
      isOnline: networkState.online,
      isProcessing: false,
      pendingOperations: [],
      queueOperation: async () => ({
        success: false,
        error: 'Missing storeName',
      }),
      forceSync: () => {},
      clearPendingOperations: async () => ({
        success: false,
        error: 'Missing storeName',
      }),
      getFailedOperations: async () => [],
      retryFailedOperation: async () => ({
        success: false,
        error: 'Missing storeName',
      }),
      clearFailedOperations: async () => ({
        success: false,
        error: 'Missing storeName',
      }),
    };
  }

  return {
    storeName,
    isOnline,
    isProcessing: processingRef.current || isProcessing,
    pendingOperations,
    reloadOperations,
    queueOperation,
    forceSync,
    clearPendingOperations,
    getFailedOperations,
    retryFailedOperation,
    clearFailedOperations,
  };
};
